/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { css as cssUnbounded, jsx, keyframes } from '@compiled/react';

import { token } from '@atlaskit/tokens';

// --- 'start' animation: full-bar sliding train of color blocks ---
const slideAnimation = keyframes({
	'0%': { left: '-30%', width: '30%' },
	'50%': { left: '25%', width: '75%' },
	'100%': { left: '130%', width: '30%' },
});

// 'planning' / 'running' animation: scroll a 200% wide inner div by -50% of its
// own width so it loops seamlessly within the fixed-width clip region.
const scrollAnimation = keyframes({
	'0%': { transform: 'translateX(-50%)' },
	'100%': { transform: 'translateX(0%)' },
});

// Order: orange, green, blue, purple
const COLORS = [
	token('color.chart.categorical.4'), // orange
	token('color.chart.categorical.2'), // green
	token('color.chart.categorical.1'), // blue
	token('color.chart.categorical.3'), // purple
];
const COMPLETE_COLOR = token('color.chart.categorical.2'); // green

export type RainbowProgressBarStatus = 'start' | 'planning' | 'running' | 'complete';

const containerStyles = cssUnbounded({
	width: '100%',
	height: '3px',
	borderRadius: token('radius.xsmall'),
	overflow: 'hidden',
	position: 'relative',
	backgroundColor: token('color.background.neutral'),
});

const containerThickStyles = cssUnbounded({
	height: '6px',
	borderRadius: token('radius.full'),
});

// 'start': sliding train of divs
const trainStyles = cssUnbounded({
	display: 'flex',
	position: 'absolute',
	height: '100%',
	animationName: slideAnimation,
	animationDuration: '1.2s',
	animationIterationCount: 'infinite',
	animationTimingFunction: 'linear',
});

const blockStyles = cssUnbounded({
	height: '100%',
	flex: '1 0 25%',
});

// Scroll train blocks are 1/8th of the inner div width (which is 200% of the clip container)
const scrollBlockStyles = cssUnbounded({
	height: '100%',
	flex: '1 0 12.5%',
});

const scrollInnerStyles = cssUnbounded({
	display: 'flex',
	height: '100%',
	width: '200%',
	animationName: scrollAnimation,
	animationDuration: '1.2s',
	animationIterationCount: 'infinite',
	animationTimingFunction: 'linear',
});

const completeStyles = cssUnbounded({
	position: 'absolute',
	inset: 0,
	backgroundColor: COMPLETE_COLOR,
});

// Clipping container for 'planning' and 'running' — width is set dynamically via inline style
const scrollRegionStyles = cssUnbounded({
	position: 'absolute',
	top: 0,
	left: 0,
	height: '100%',
	overflow: 'hidden',
});

// Determinate mode: width is driven by the `progress` prop. Parents are
// responsible for animating it smoothly (e.g. via requestAnimationFrame).
const determinateRegionStyles = cssUnbounded({
	position: 'absolute',
	top: 0,
	left: 0,
	height: '100%',
	overflow: 'hidden',
});

// Static rainbow: same 4 color blocks as the animated version, no scroll.
const staticRainbowFillStyles = cssUnbounded({
	display: 'flex',
	height: '100%',
	width: '100%',
});

const solidFillStyles = cssUnbounded({
	position: 'absolute',
	inset: 0,
});

/**
 * Determinate-mode fill style:
 * - `'rainbow'`: scrolling rainbow color blocks
 * - `'static-rainbow'`: static rainbow gradient
 * - any other string: a solid CSS color (e.g. a design token value)
 */
export type RainbowProgressBarAppearance = 'rainbow' | 'static-rainbow' | (string & {});

type RainbowProgressBarThickness = 'default' | 'thick';

type RainbowProgressBarProps = {
	testId?: string;
	status?: RainbowProgressBarStatus;
	label?: string;
	/** 0-100. When provided, the bar is determinate; otherwise `status` drives the indeterminate animation. */
	progress?: number;
	/** Determinate-mode fill style; ignored when `progress` is undefined. */
	appearance?: RainbowProgressBarAppearance;
	/**
	 * Bar thickness.
	 * - `'default'` (3px)
	 * - `'thick'` (6px)
	 * @default 'default'
	 */
	thickness?: RainbowProgressBarThickness;
};

const ColorTrain = () => (
	<div css={trainStyles}>
		{COLORS.map((color) => (
			<div key={color} css={blockStyles} style={{ backgroundColor: color }} />
		))}
	</div>
);

const ScrollTrainInner = () => (
	<div css={scrollInnerStyles}>
		{/* Two full copies of the color train for seamless looping */}
		{[0, 1].map((copy) =>
			COLORS.map((color) => (
				<div key={`${copy}-${color}`} css={scrollBlockStyles} style={{ backgroundColor: color }} />
			)),
		)}
	</div>
);

const ARIA_VALUE_TEXT: Partial<Record<RainbowProgressBarStatus, string>> = {
	planning: 'Planning...',
	running: 'Running...',
};

const StaticRainbowFill = () => (
	<div css={staticRainbowFillStyles}>
		{COLORS.map((color) => (
			<div key={color} css={blockStyles} style={{ backgroundColor: color }} />
		))}
	</div>
);

const DeterminateFill = ({ appearance }: { appearance: RainbowProgressBarAppearance }) => {
	if (appearance === 'rainbow') {
		return <ScrollTrainInner />;
	}
	if (appearance === 'static-rainbow') {
		return <StaticRainbowFill />;
	}
	// Treat any other string as a CSS color.
	return <div css={solidFillStyles} style={{ backgroundColor: appearance }} />;
};

const RainbowProgressBar = ({
	testId,
	status = 'start',
	label = 'Loading',
	progress,
	appearance = 'rainbow',
	thickness = 'default',
}: RainbowProgressBarProps): JSX.Element => {
	const isDeterminate = progress !== undefined;
	const isComplete = status === 'complete';

	// Indeterminate states: omit aria-valuenow/min/max (per the ARIA spec) and
	// surface contextual feedback via aria-valuetext for planning/running.
	const ariaValueText = isDeterminate ? undefined : ARIA_VALUE_TEXT[status];
	const clampedProgress = isDeterminate ? Math.min(100, Math.max(0, progress)) : undefined;

	return (
		<div
			css={[containerStyles, thickness === 'thick' && containerThickStyles]}
			data-testid={testId}
			role="progressbar"
			aria-label={label}
			aria-valuetext={ariaValueText}
			aria-valuenow={isDeterminate ? Math.round(clampedProgress!) : isComplete ? 100 : undefined}
			aria-valuemin={isDeterminate || isComplete ? 0 : undefined}
			aria-valuemax={isDeterminate || isComplete ? 100 : undefined}
		>
			{isDeterminate ? (
				<div css={determinateRegionStyles} style={{ width: `${clampedProgress!}%` }}>
					<DeterminateFill appearance={appearance} />
				</div>
			) : isComplete ? (
				<div css={completeStyles} />
			) : status === 'planning' || status === 'running' ? (
				<div css={scrollRegionStyles} style={{ width: status === 'planning' ? '30%' : '60%' }}>
					<ScrollTrainInner />
				</div>
			) : (
				// Full-bar sliding train (start).
				<ColorTrain />
			)}
		</div>
	);
};

export { RainbowProgressBar };
